import { useState, type FormEvent } from 'react'
import { ActionGlyph } from '../../components/ActionGlyph'
import { focusFirstInvalid } from '../../lib/forms'
import { useAppActions } from '../../state/AppProvider'
import { AccountScaffold } from './AccountScaffold'

interface RecoveryPageProps {
  navigate: (to: string) => void
}

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function RecoveryPage({ navigate }: RecoveryPageProps) {
  const actions = useAppActions()
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [checked, setChecked] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = event.currentTarget
    if (!emailPattern.test(email.trim())) {
      setError('Enter a complete email address.')
      setChecked(false)
      focusFirstInvalid(form)
      return
    }
    setError('')
    setSubmitting(true)
    try {
      await actions.recover(email.trim())
      setChecked(true)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AccountScaffold
      eyebrow="Account · Recovery"
      title="Find your way back"
      intro="Check whether this device has an account saved under your email address."
      onBack={() => navigate('/auth/sign-in')}
    >
      <form className="access-form" noValidate onSubmit={handleSubmit} aria-busy={submitting}>
        <div className="access-field">
          <label htmlFor="recovery-email">Email</label>
          <input
            autoCapitalize="none"
            autoComplete="email"
            id="recovery-email"
            inputMode="email"
            spellCheck={false}
            type="email"
            value={email}
            aria-describedby={error ? 'recovery-email-error' : 'recovery-email-hint'}
            aria-invalid={Boolean(error)}
            onChange={(event) => {
              setEmail(event.target.value)
              if (error) setError('')
            }}
          />
          {error ? (
            <p className="access-error" id="recovery-email-error">{error}</p>
          ) : (
            <p className="access-hint" id="recovery-email-hint">The response is the same whether an account is found or not.</p>
          )}
        </div>

        {checked ? (
          <div className="access-status access-status--success" role="status">
            <strong>Check complete</strong>
            <span>No email was sent. If a matching account is saved here, return to sign in with the password you created.</span>
          </div>
        ) : <div className="access-status-slot" aria-live="polite" />}

        <div className="access-actions">
          <button className="access-primary" disabled={submitting} type="submit">
            {submitting ? 'Checking…' : 'Check this device'}
            <ActionGlyph />
          </button>
          <button className="access-secondary" type="button" onClick={() => navigate('/auth/sign-in')}>
            Return to sign in
            <ActionGlyph direction="back" />
          </button>
          <button className="access-text-button public-support-link" type="button" onClick={() => navigate('/support')}>
            Open Support
          </button>
        </div>
      </form>
    </AccountScaffold>
  )
}
